import { Navigate, Route, Routes } from 'react-router-dom'

import { AppShell } from './components/AppShell'
import { AuthGate } from './components/AuthGate'
import { Calendar } from './routes/Calendar'
import { useIsMobile } from './lib/mobile'
import { MobileApp } from './mobile/MobileApp'
import { useTheme } from './lib/useTheme'
import { Appearance } from './routes/Appearance'
import { Account } from './routes/Account'
import { PrivacyData } from './routes/PrivacyData'
import { JournalDefaults } from './routes/JournalDefaults'
import { Notifications } from './routes/Notifications'
import { Chat } from './routes/Chat'
import { Scratchpad } from './routes/Scratchpad'
import { Connections } from './routes/Connections'
import { Interests } from './routes/Interests'
import { Today } from './routes/Today'
import { Day } from './routes/Day'
import { DayWeeklyCheck } from './routes/DayWeeklyCheck'
import { Journal } from './routes/Journal'
import { JournalDay } from './routes/JournalDay'
import { School } from './routes/School'
import { Search } from './routes/Search'
import { SchoolCourse } from './routes/SchoolCourse'
import { SchoolLecture } from './routes/SchoolLecture'
import { RecordLecture } from './routes/RecordLecture'
import { Recording } from './routes/Recording'
import { Tasks } from './routes/Tasks'
import { Learning } from './routes/Learning'
import { LearningTrack } from './routes/LearningTrack'

/**
 * The routes behind the rail (docs/design/handoff/README.md → Interactions & Behavior:
 * `/today`, `/journal`, `/school`, `/tasks`, `/learning`, `/interests`,
 * `/settings/connections`, plus `/chat` from the orchestrator decision in
 * docs/design/FIDELITY.md). `/search` is NOT a rail item — it is reached from the header
 * search field — but the route stays, because the Search screens are designed and land in V4.
 *
 * Today is real; the rest are honest placeholders that name the version they arrive in, so
 * nobody — owner or agent — mistakes an empty screen for a broken one.
 *
 * Everything below AuthGate assumes a data source: the demo dataset when nothing is configured, a
 * signed-in session otherwise. No screen inside it ever has to handle "not signed in".
 */
export function App() {
  // MOUNTED HERE AND NOWHERE ELSE THAT MATTERS: above AuthGate, so the login screen wears the
  // theme too, and on every screen rather than only on the one with the control — a 'system'
  // choice has to follow the machine when Windows flips at sunset, whatever route is open.
  // The <html> stamp itself happened before React booted (the inline script in index.html); this
  // keeps it, and `<meta name="theme-color">`, true for the rest of the session.
  useTheme()
  return (
    <AuthGate>
      <SignedInApp />
    </AuthGate>
  )
}

/**
 * THE <600px SWITCH — docs/design/handoff-mobile/README.md §Viewport and device:
 * "Above 600 px, do not stretch — the app switches to the desktop layout."
 *
 * It is a COMPONENT switch, not a stylesheet: the mobile app is a different and
 * deliberately smaller product (five tabs, five screens, five desktop screens
 * absent by decision), and hiding one tree with CSS would mean mounting both and
 * fetching for both. src/lib/mobile.ts carries the reasoning and the rule that
 * anything which cannot answer the media query gets the desktop app.
 */
function SignedInApp() {
  if (useIsMobile()) return <MobileApp />
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Today />} />
        {/* THE DAY PAGE (Lane 4a, docs/design/handoff/day-page/): its own rail entry after Today.
            The weekly check is its own route — "Day ↔ Weekly check: an instant cut" (14h). The
            phone shell still mounts the prototype at `/day` (mobile/MobileApp.tsx). In demo mode
            `?state=morning|quiet|degraded|first-run|loading` selects a board state. */}
        <Route path="/day" element={<Day />} />
        <Route path="/day/weekly" element={<DayWeeklyCheck />} />
        {/* The Journal port: the archive (5a) and one day fully opened (5b). The day route takes
            any calendar date, not only dates that carry an entry — the day you missed opens too,
            and says so. */}
        <Route path="/journal" element={<Journal />} />
        <Route path="/journal/:date" element={<JournalDay />} />
        {/* School is three screens, not one: the overview (6a), one course (6b) and one lecture
            in study mode (6c). They run on demo data today; the Canvas connector (V2) and the
            transcription system (V1) fill them from the real thing, and until then remote.ts
            throws a named error per method that each card prints in place. */}
        <Route path="/school" element={<School />} />
        <Route path="/school/:courseCode" element={<SchoolCourse />} />
        <Route path="/school/:courseCode/lecture/:n" element={<SchoolLecture />} />
        {/* The phone's way in. `/record` is MOBILE-FIRST and has no mockup — the handoff is ten
            desktop screens and none of them is this one; it is reached from the School header and
            from the manifest's share target, which is how Samsung Voice Recorder's Share sheet
            ends up here with the file already picked. `/recordings/:id` addresses a recording by
            ID rather than by course-and-number, because a recording from the desktop drop folder
            has neither until someone gives it one — which is the write that page exists for. */}
        <Route path="/record" element={<RecordLecture />} />
        <Route path="/recordings/:id" element={<Recording />} />
        {/* Tasks (8a) runs on demo data: reading, completing and adding are all real against the
            in-memory dataset. In remote mode every tasks method throws a NotImplementedError
            naming V6 and each column prints that sentence — an empty board would claim "you have
            nothing to do", which is a different and false statement. */}
        {/* Calendar (11a week / 11b day) is real in both modes: the grid reads LifeOS blocks and
            derived, read-only Canvas deadlines out of one ranged endpoint, and creating, moving
            and deleting blocks all write. Month is a deliberate stub — docs/design/calendar-brief.md
            says ship Day + Week and leave the third toggle honest about itself. */}
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/tasks" element={<Tasks />} />
        {/* Interests (7a) runs on demo data today. The screen is real; the capture endpoint and
            the Reddit backlog import behind it are V5, and the ask bar's engine is V4 — so each
            region names its own version rather than rendering an empty library. */}
        <Route path="/interests" element={<Interests />} />
        {/* Learning is two screens: the overview (9a) and one track opened (9b). They run on demo
            data today; the track-ingestion API (V3) fills them from real imported plans, and until
            then remote.ts throws a named error per method that each panel prints in place. */}
        <Route path="/learning" element={<Learning />} />
        <Route path="/learning/:trackId" element={<LearningTrack />} />
        {/* Search (10a/10b) runs on demo data today, and the literal half of it is a REAL search
            over the whole dataset — journal, transcripts, saves, tasks, deadlines, syllabus
            facts, learning items. The related half cannot be real without embeddings, so it is a
            curated list for the two queries the handoff designs and an honest "arrives with V4"
            line for every other one. In remote mode `search` throws a named error and the results
            area prints it: an empty list would claim "you have nothing like that". */}
        <Route path="/search" element={<Search />} />
        {/* Chat (11a/11b) is LIVE since V4, in demo mode and against the real API: the thread
            list, thread selection, scope switching, sending a message and the model's answer —
            every source card resolved against a real entity and linking to its real route — all
            go through the same engine (`remote.ts` → `sendChatMessage`,
            apps/worker/src/routes/chat.ts). */}
        <Route path="/chat" element={<Chat />} />

        {/* THE SCRATCHPAD — a holding pen, deliberately server-less. It stores nothing an LLM
            needs to read back (routes/Scratchpad.tsx says why at length), and it states its own
            three limits on screen rather than looking like the synced screens around it. */}
        <Route path="/scratchpad" element={<Scratchpad />} />
        {/* Connections is built EARLY on demo data (handoff §Suggested build order 4:
            "every other screen's honest-degradation state depends on connector and worker
            status being real"). The screen is real; the four backends it reports on are
            not, and each panel says which version brings its own. */}
        <Route path="/settings/connections" element={<Connections />} />
        {/* Appearance is the second settings screen and the home of the theme control
            (light / dark / system). The handoff draws neither the screen nor a dark
            palette — both are the owner's 2026-08-26 request, built on the Connections
            screen's own parts rather than a second settings idiom. */}
        <Route path="/settings/appearance" element={<Appearance />} />
        {/* The other four settings rows, built 2026-08-28. Three of them are mostly a statement
            of what does NOT exist — no profile to edit in a single-user app, no data export
            endpoint, no push pipeline — and each says so in words rather than shipping a control
            with nothing behind it. Journal defaults is the exception: its two settings are wired
            to the composer through a browser-local store, the same way the theme is. */}
        <Route path="/settings/account" element={<Account />} />
        <Route path="/settings/privacy" element={<PrivacyData />} />
        <Route path="/settings/journal" element={<JournalDefaults />} />
        <Route path="/settings/notifications" element={<Notifications />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  )
}
